import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import StatusIndicator, {
  type StatusIndicatorProps,
} from '@cloudscape-design/components/status-indicator';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import coverage from '@etiquetachef/i18n/coverage.json';
import {
  subscribeMarketConfigs,
  type MarketConfigListResult,
} from '@/lib/firestoreTenantDetail';
import { flagEmoji } from './worldCountries';

/** Status de um idioma numa plataforma — o vocabulário do manifesto. */
export type CoverageStatus = 'full' | 'override' | 'fallback' | 'missing';
const PLATFORMS = ['web', 'android', 'ios', 'email', 'site'] as const;
type Platform = (typeof PLATFORMS)[number];

/** Uma linha do manifesto `packages/i18n/coverage.json`. */
export type CoverageEntry = {
  locale: string;
  kind: 'full' | 'regional';
  base?: string;
  platforms: Record<Platform, CoverageStatus>;
};

// País da bandeira de cada idioma. Regional usa a própria região; idioma sem
// região usa a origem mais comum. `fr` e `de` faltavam no mapa antigo e o `fr`
// aparecia sem bandeira (#957).
const FLAG_REGION: Readonly<Record<string, string>> = {
  'pt-BR': 'BR',
  en: 'US',
  es: 'ES',
  it: 'IT',
  fr: 'FR',
  de: 'DE',
};

const STATUS_TYPE: Readonly<Record<CoverageStatus, StatusIndicatorProps.Type>> = {
  full: 'success',
  override: 'info',
  fallback: 'stopped',
  missing: 'error',
};

/**
 * Aba Idiomas de `/countries`: matriz idioma × plataforma (web, Android, iOS,
 * e-mail, site) e quantos países do catálogo usam cada idioma.
 *
 * Origem: #957 — 2026-10-13 12:05 +0200 (owner: "aba idiomas para todos os
 * implementados no sistema"; antes era uma lista inline em `CountriesPage`
 * com o `SUPPORTED` do admin).
 *
 * Por quê: o admin roda no navegador e não lê os arquivos de string do
 * Android/iOS. A verdade de cada plataforma vem do manifesto gerado no commit
 * (`scripts/gen-locale-coverage.mjs`), que tem gate no `guards.yml` — a tela
 * mostra o que está no código publicado, não uma lista à mão.
 *
 * Contrato: lê `@etiquetachef/i18n/coverage.json` (estático, no bundle) e
 * assina `config/markets/items` só para contar `language` por idioma. Só
 * leitura; nenhuma escrita.
 *
 * Usado por: `routes/CountriesPage.tsx` (aba Idiomas de `/countries`, admin
 * web).
 *
 * Arquitetura: a lista de idiomas é `TenantLocaleSchema`
 * (`packages/types/src/schemas.ts`); o manifesto a segue. A contagem de países
 * é informativa: país sem `language` no doc não conta (ele usa o default
 * compilado — `resolveMarketConfig`).
 *
 * Dados: `coverage.json` — `{ locales: [{ locale, kind, base?, platforms:
 * { web|android|ios|email|site: full|override|fallback|missing } }] }`;
 * `config/markets/items/{ISO-2}.language` (`TenantLocaleSchema`, opcional).
 *
 * Dicas: idioma novo não precisa de mudança aqui — entra no enum, ganha os
 * arquivos de string e o `gen-locale-coverage.mjs` o põe no manifesto.
 *
 * Issues: #957 (aba), #86 (alemão + regionais nos apps).
 */
export function LanguagesTab(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [markets, setMarkets] = useState<MarketConfigListResult>({ kind: 'LOADING' });

  useEffect(() => subscribeMarketConfigs(setMarkets), []);

  const entries = coverage.locales as readonly CoverageEntry[];

  // Países do catálogo por idioma (`market.language`).
  const marketCount = useMemo(() => {
    const map = new Map<string, number>();
    if (markets.kind !== 'READY') return map;
    for (const m of markets.markets) {
      if (!m.language) continue;
      map.set(m.language, (map.get(m.language) ?? 0) + 1);
    }
    return map;
  }, [markets]);

  const columnDefinitions = useMemo<readonly TableProps.ColumnDefinition<CoverageEntry>[]>(() => {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([i18n.language], { type: 'language' });
    } catch {
      // Runtime sem `Intl.DisplayNames` para o locale: mostra só o código.
    }
    const statusCell = (e: CoverageEntry, p: Platform): React.ReactNode => {
      const s = e.platforms[p];
      return (
        <StatusIndicator type={STATUS_TYPE[s]}>
          {t(`admin.countries.languages.status.${s}`, { base: e.base ?? '' })}
        </StatusIndicator>
      );
    };
    return [
      {
        id: 'flag',
        header: t('admin.countries.languages.flag'),
        cell: (e): React.ReactNode => {
          const region = FLAG_REGION[e.locale] ?? e.locale.split('-')[1] ?? '';
          return <span style={{ fontSize: 20 }}>{region ? flagEmoji(region) : ''}</span>;
        },
      },
      {
        id: 'code',
        header: t('admin.countries.languages.code'),
        cell: (e): React.ReactNode => (
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.locale}</span>
        ),
      },
      {
        id: 'name',
        header: t('admin.countries.languages.name'),
        cell: (e): React.ReactNode => names?.of(e.locale) ?? e.locale,
      },
      {
        id: 'kind',
        header: t('admin.countries.languages.kind'),
        cell: (e): React.ReactNode =>
          e.kind === 'regional'
            ? t('admin.countries.languages.kindRegional', { base: e.base ?? '' })
            : t('admin.countries.languages.kindFull'),
      },
      ...PLATFORMS.map(
        (p): TableProps.ColumnDefinition<CoverageEntry> => ({
          id: p,
          header: t(`admin.countries.languages.columns.${p}`),
          cell: (e): React.ReactNode => statusCell(e, p),
        }),
      ),
      {
        id: 'markets',
        header: t('admin.countries.languages.columns.markets'),
        cell: (e): React.ReactNode =>
          markets.kind === 'READY' ? String(marketCount.get(e.locale) ?? 0) : '—',
      },
    ];
  }, [t, i18n.language, markets.kind, marketCount]);

  return (
    <Table<CoverageEntry>
      items={[...entries]}
      trackBy="locale"
      variant="container"
      wrapLines
      header={
        <Header
          counter={`(${String(entries.length)})`}
          description={t('admin.countries.languages.description')}
        >
          {t('admin.countries.tabs.languages')}
        </Header>
      }
      footer={
        <Box color="text-status-inactive" fontSize="body-s">
          {t('admin.countries.languages.legend')}
        </Box>
      }
      columnDefinitions={columnDefinitions}
    />
  );
}
